"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AdjustmentValidator = void 0;
var _types = require("@/types");
var _utils = require("@/utils/utils");
var _constants = require("@/constants");
var _apiStateClient = require("@abacus/api-state-client");
class AdjustmentValidator {
  constructor({
    cache,
    enableCloseBalanceStatuses,
    enableStatementDate
  }) {
    this.cache = cache;
    this.enableCloseBalanceStatuses = enableCloseBalanceStatuses ?? true;
    this.enableStatementDate = enableStatementDate ?? true;
  }
  validate(adjustment) {
    const errors = [];
    this.validateAccountId(adjustment, errors);
    this.validateActivityDate(adjustment, errors);
    this.validateAdjustmentType(adjustment, errors);
    this.validateCloseBalanceStatus(adjustment, errors);
    this.validateContractId(adjustment, errors);
    this.validateStatementDate(adjustment, errors);
    this.validateUpc(adjustment, errors);
    return errors.length > 0 ? {
      errors
    } : {
      data: true
    };
  }
  validateAccountId(adjustment, errors) {
    const accountId = adjustment.accountId;
    if (!this.cache.accountIds.has(accountId)) {
      errors.push(`${accountId} Account ID does not exist in Abacus`);
    }
  }
  validateActivityDate(adjustment, errors) {
    const month = adjustment.activityMonth;
    const year = adjustment.activityYear;
    if (!(0, _utils.doubleMapHas)(this.cache.statementPeriods, year, month)) {
      errors.push("Activity period does not exist in Abacus");
    }
  }
  validateAdjustmentType(adjustment, errors) {
    const adjustmentType = adjustment.adjustmentType;
    if (!this.cache.adjustmentTypes.has(adjustmentType)) {
      errors.push("Adjustment type is not supported by Abacus");
    } else if (adjustmentType === _types.AdjustmentType.AccountExpense) {
      errors.push('"Account Expense" is not an allowed adjustment type');
    }
  }
  validateCloseBalanceStatus(adjustment, errors) {
    const accountId = adjustment.accountId;
    const paymentEntityId = this.cache.accountPaymentEntityIds.get(accountId);
    if (paymentEntityId == null) {
      errors.push("Error when validating closed balance: account has no payment entity");
      return;
    }
    const closeBalanceStatus = this.cache.paymentEntityCloseBalanceStatuses.get(paymentEntityId);
    if (closeBalanceStatus == null) {
      errors.push("Error when validating closed balance: account has no close balance");
    } else if (closeBalanceStatus == _apiStateClient.AbacusStateActionStatus.Complete) {
      if (this.enableCloseBalanceStatuses) {
        errors.push("Contract's close balance is completed for the provided 'Apply To Statement Year' and 'Apply To Statement Month'");
      }
    }
  }
  validateContractId(adjustment, errors) {
    if (!this.hasContractId(adjustment)) {
      const accountId = adjustment.accountId;
      const contractId = adjustment.contractId;
      errors.push(`Contract ${contractId} does not exist in Abacus for account ${accountId}`);
    }
  }
  hasContractId(adjustment) {
    const accountId = adjustment.accountId;
    const contractId = adjustment.contractId;
    const contractIds = this.cache.accountContractIds;
    return (0, _utils.setMapHas)(contractIds, accountId, contractId);
  }
  validateStatementDate(adjustment, errors) {
    const month = adjustment.statementMonth;
    const year = adjustment.statementYear;
    const period = this.cache.statementPeriods.get(year)?.get(month);
    if (period == null || !_constants.STATEMENT_PERIOD_STATUSES.has(period.status)) {
      if (this.enableStatementDate) {
        errors.push("The Abacus statement period is missing, closed, or in the future.");
      }
    }
  }

  /**
   * Checks if a UPC is attached to an account.
   *
   * Originally, UPC was checked against the contract and fell back to the account level.
   * To reduce complexity and improve performance, UPC ownership is flattened to only
   * check at the account level.
   *
   * Original:
   * 1. Verifies if upc is attached to a contract.
   * 2. If 1st step fails then removes the first/all leading zeros from the upc and
   *     then verifies if upc is attached to a contract.
   * 3. If 2nd step fails then checks if upc is "display upc"(gets display upc from art-relation db).
   *     if yes then gets the value of upc for display upc and check if that upc is attached to a contract.
   * 4. If 3rd step fails then check if the account_id mentioned in the sheet owns that upc if the account_id
   *     appears in the contract label terms.
   */
  validateUpc(adjustment, errors) {
    if (adjustment.upc == null) {
      return;
    }
    const accountId = adjustment.accountId;
    const upc = adjustment.upc.replace(/^0+/, "");
    if (!(0, _utils.setMapHas)(this.cache.accountUpcs, accountId, upc)) {
      errors.push(`UPC ${adjustment.upc} is not on this contract or account`);
    }
  }
}
exports.AdjustmentValidator = AdjustmentValidator;